// ---------------------------------------------------------------------------
// Audit Log - filter bar
//
// Groups the search input, resource/action selects, date range, user name
// filter and reset button together. All filter state lives on the parent
// page; this component only renders controls and forwards user input.
// ---------------------------------------------------------------------------

import { Search } from "lucide-react";
import { ACTION_TYPE_OPTIONS, RESOURCE_TYPE_OPTIONS } from "./constants";

interface AuditFiltersProps {
  readonly searchInput: string;
  readonly onSearchInputChange: (value: string) => void;
  readonly actionFilter: string;
  readonly onActionFilterChange: (value: string) => void;
  readonly resourceFilter: string;
  readonly onResourceFilterChange: (value: string) => void;
  readonly dateFrom: string;
  readonly onDateFromChange: (value: string) => void;
  readonly dateTo: string;
  readonly onDateToChange: (value: string) => void;
  readonly userFilter: string;
  readonly onUserFilterChange: (value: string) => void;
  readonly hasActiveFilters: boolean;
  readonly onReset: () => void;
}

export function AuditFilters({
  searchInput,
  onSearchInputChange,
  actionFilter,
  onActionFilterChange,
  resourceFilter,
  onResourceFilterChange,
  dateFrom,
  onDateFromChange,
  dateTo,
  onDateToChange,
  userFilter,
  onUserFilterChange,
  hasActiveFilters,
  onReset,
}: AuditFiltersProps) {
  return (
    <div className="rounded-lg border border-stone-200 bg-white p-4">
      <div className="flex flex-wrap items-center gap-3">
        {/* Search */}
        <div className="relative min-w-[200px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => onSearchInputChange(e.target.value)}
            placeholder="Rechercher par nom, ressource, ID..."
            className="w-full rounded-md border border-stone-200 py-1.5 pl-9 pr-3 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          />
        </div>

        {/* Resource filter */}
        <select
          value={resourceFilter}
          onChange={(e) => onResourceFilterChange(e.target.value)}
          className="rounded-md border border-stone-200 px-3 py-1.5 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
        >
          {RESOURCE_TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        {/* Action filter */}
        <select
          value={actionFilter}
          onChange={(e) => onActionFilterChange(e.target.value)}
          className="rounded-md border border-stone-200 px-3 py-1.5 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
        >
          {ACTION_TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        {/* Date range */}
        <div className="flex items-center gap-1.5">
          <label className="text-xs text-stone-500">Du</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => onDateFromChange(e.target.value)}
            className="rounded-md border border-stone-200 px-2 py-1.5 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          />
          <label className="text-xs text-stone-500">au</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => onDateToChange(e.target.value)}
            className="rounded-md border border-stone-200 px-2 py-1.5 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          />
        </div>

        {/* User filter */}
        <input
          type="text"
          value={userFilter}
          onChange={(e) => onUserFilterChange(e.target.value)}
          placeholder="Filtrer par utilisateur"
          className="w-40 rounded-md border border-stone-200 px-3 py-1.5 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
        />

        {/* Reset */}
        {hasActiveFilters && (
          <button
            onClick={onReset}
            className="rounded-md px-2.5 py-1.5 text-xs font-medium text-stone-500 hover:bg-stone-100 hover:text-stone-700"
          >
            Reinitialiser
          </button>
        )}
      </div>
    </div>
  );
}
